// Call history (UX spec 6.3 S1 "Calls" view) and call detail permalink.
import type { Call, Recording } from '@bcio-voice/contracts';
import { useEffect, useMemo, useState,type ReactNode } from 'react';
import { ArrowDownLeft, ArrowUpRight, ArrowLeftRight, Ban, Captions, ChevronLeft, Download, ExternalLink, Radio, Voicemail } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { ErrorPanel, SkeletonRows, StatePanel } from '../../ui/StatePanel.tsx';
import { CallLineIdentity, resolveCallLine } from '../../ui/CallLineIdentity.tsx';
import { type HistoryFilter, type WorkspaceValue, useWorkspace } from '../../state/workspace.tsx';
import { Link, useRouter } from '../../lib/router.tsx';
import { formatAbsolute, formatDuration, formatPhone, formatRelative, parsePhoneInput, phoneParseMessage } from '../../lib/format.ts';
import { BlocklistClient, type CallNote, type CollectionEnvelope, type DataEnvelope, isAbortError, query } from '../../lib/api.ts';
import { describeError, type ErrorView } from '../../lib/errorCopy.ts';
import { capability, gate, hasPermission } from '../../lib/capabilities.ts';
import { useSensitiveResource } from '../../state/useSensitiveResource.ts';
import { CallSummarySection, callOutcomeText } from './callSummary.tsx';
import { CaptureBand } from '../phone/CaptureBand.tsx';
import { CoachPanel } from '../../call/coach/CoachPanel.tsx';
import { callWindowSupported } from '../callWindow/callWindowLaunch.ts';
import { Diagnostics } from '../../ui/Diagnostics.tsx';
import { gateDiagnostic } from '../../lib/userCopy.ts';
import { callCrmLink, callWho, directionLabel, endedByLine, isOperatorMode, partyName, recorderNode } from '../phone/callView.ts';
import { TRANSCRIPTS_ONLY_TEXT, transcriptsOnly } from '../../call/mediaStatus.ts';
import { RecordingMedia, retentionLine } from '../recordings/RecordingPlayer.tsx';
import { coalesceCallRecordings, type RecordingWithProvenance } from '../recordings/archive.ts';
import { CallTranscriptBody, transcriptStatusLine, useCallTranscript } from '../transcript/TranscriptView.tsx';
import { AskPanel } from '../transcript/AskPanel.tsx';
import { TerminationLabel, WhatHappenedSection } from '../timeline/WhatHappened.tsx';
import { BlocklistInlineStyles } from '../blocklist/InlineStyles.tsx';
import { NumberStatus } from '../blocklist/NumberStatus.tsx';
import { type CallBlock, LIST_LABEL, blockSentence, blocklistAccess, callBlock, callNumber, clipReason } from '../blocklist/model.ts';
import { CallNotesSection } from './CallNotes.tsx';
import { CallContactNameEditor } from './CallContactNameEditor.tsx';
import { PostCallFollowupSection } from '../phone/PostCallFollowup.tsx';
import { type CallDrop, dropBadge, dropResultText, effectiveDrop, playedText, useLocalDrops } from '../phone/voicemailDrop.ts';
import { VoicemailDropStyles } from '../voicemail/DropStyles.tsx';
import {emptyHistoryFilter,matchesHistory} from './historyQuery.ts';
import {historyHref,historyDetailHref,historyFilterKey,parseHistorySearch,localDateInput,dateInputIso,unansweredScope} from './historyRoute.ts';
import { useBackgroundRefresh } from '../../state/refreshActivity.ts';
import { StaleNote, streamIsDown } from '../../ui/StaleNote.tsx';
import { CallbackButton } from './CallbackButton.tsx';
import {PhoneNumberActions} from '../../ui/PhoneNumberActions.tsx';

/** The outcome word for the ledger and detail: a refused call reads "Blocked", a call with a drop "Voicemail dropped". */
function outcomeText(call: Call, block: CallBlock | null, drop: CallDrop | null = null): string {
  return callOutcomeText(call, block, drop);
}

/** Prefilled note when blocking from a call, so the entry's history says where it came from. */
function blockNote(call: Call, timeZone?: string): string {
  const kind = call.direction === 'inbound' ? 'an incoming' : 'an outgoing';
  return `Blocked from ${kind} call on ${formatAbsolute(call.created_at, timeZone)}.`;
}

function formatLogTimestamp(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

function formatCsvLocalTime(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

function csvEscape(val: string | number | null | undefined): string {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (/[",\r\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function callRepName(call: Call, ws: WorkspaceValue): string {
  const agents = call.participants.filter(p => p.kind === 'agent' && p.user_id);
  const connected = agents.find(p => p.joined_at !== null) ?? agents[0];
  const uid = connected?.user_id ?? (call.destination.kind === 'internal' ? call.destination.user_id : null);
  if (uid) {
    const lookedUp = ws.userName(uid);
    if (lookedUp) return lookedUp;
    if (uid === ws.userId && ws.session.user?.display_name) return ws.session.user.display_name;
  }
  const line = resolveCallLine(call, ws.numbers.value);
  if (line && line.label && !/^Spare\b/i.test(line.label)) return line.label;
  return uid === ws.userId ? (ws.session.user?.display_name ?? 'You') : 'Unassigned';
}

export function CallsPage({ callId }: { callId: string | null }) {
  const ws = useWorkspace();
  const { search } = useRouter();
  const isLogTab = search.get('tab') === 'log';
  const canRead = ['calls:read_own', 'calls:read_team', 'calls:read_all'].some(p => hasPermission(ws.permissions, p));
  if (!canRead) {
    return (
      <div className="page">
        <h1 className="page-title">Calls</h1>
        <StatePanel variant="denied" title="You don't have access to call history" body="Your role doesn't include reading calls. Ask an admin if you need it." details={{ permission: 'calls:read_own' }} />
      </div>
    );
  }
  return (
    <div className={`split${callId ? ' has-detail' : ''}${isLogTab && !callId ? ' is-log-view' : ''}`}>
      <section className="list-pane" aria-labelledby="calls-title">
        <CallList selectedId={callId} />
      </section>
      <section className="detail-pane" aria-label="Call detail">
        {callId ? <CallDetail callId={callId} /> : <StatePanel variant="empty" title="Select a call" body="Choose a call from the list to see its participants, what happened, recording and transcript." />}
      </section>
    </div>
  );
}

function CallList({ selectedId }: { selectedId: string | null }) {
  const ws = useWorkspace();
  const { search, query: routeQuery, navigate } = useRouter();
  const { history } = ws;
  const drops = useLocalDrops();
  const parsed=useMemo(()=>parseHistorySearch(search),[routeQuery]);
  const filter=parsed.filter,logView=parsed.view==='log';
  const repFilter=filter.user_id??'';
  const [exporting, setExporting] = useState(false);
  const [phoneSearch,setPhoneSearch]=useState(filter.phone_number??'');
  const [phoneError,setPhoneError]=useState('');
  useEffect(()=>{setPhoneSearch(filter.phone_number??'');setPhoneError('');},[filter.phone_number]);
  useEffect(()=>{
    if(!parsed.error&&historyFilterKey(history.filter)!==historyFilterKey(filter))ws.loadHistory(filter);
    else if(!parsed.error&&!history.loaded&&!history.loading)ws.loadHistory(filter);
  },[routeQuery,ws.loadHistory,history.filter]);
  const setFilter=(patch:Partial<HistoryFilter>,view:'list'|'log'=parsed.view)=>{
    const next={...filter,...patch};
    if('outcome' in patch){next.outcome_group=undefined;next.final_only=undefined;}
    navigate(historyHref(next,view));
  };
  const category=(direction:HistoryFilter['direction'],outcome:HistoryFilter['outcome']='')=>setFilter({direction,state:'',outcome,outcome_group:undefined,final_only:undefined,duration:undefined},'list');
  const reps = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of ws.roster.value ?? []) {
      if (u.id && u.display_name) map.set(u.id, u.display_name);
    }
    for (const a of ws.agents.value ?? []) {
      const name = ws.userName(a.user_id);
      if (a.user_id && name) map.set(a.user_id, name);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [ws.roster.value, ws.agents.value, ws.userName]);
  const rows = useMemo(() => {
    const ids = [...new Set(history.ids)];
    // A drop this tab started moves its call into the ledger at once, before the event stream says so.
    for (const [callId, drop] of drops) if (history.loaded && !Object.values(filter).some(Boolean) && drop.phase === 'started' && !ids.includes(callId) && ws.calls.has(callId)) ids.unshift(callId);
    return ids.map(id => ws.calls.get(id)).filter((c): c is Call => !!c)
      .filter(c => !parsed.error && historyFilterKey(history.filter)===historyFilterKey(filter) && matchesHistory(c,filter));
  }, [history.ids, history.filter, history.loaded, ws.calls, drops, routeQuery]);
  const filtered=Object.values(filter).some(value=>value!==undefined&&value!==''&&value!==false);
  const isAllTab=!filter.direction&&!filter.outcome&&!filter.state&&!filter.outcome_group&&!logView;
  const isMissedTab=!logView&&filter.outcome==='missed'&&filter.direction==='inbound';
  const isNotConnectedTab=!logView&&filter.direction==='outbound'&&filter.outcome_group==='not_connected';
  const isInboundTab=!logView&&filter.direction==='inbound'&&!filter.outcome&&!filter.state&&!filter.outcome_group;
  const isOutboundTab=!logView&&filter.direction==='outbound'&&!filter.outcome&&!filter.state&&!filter.outcome_group;
  const timeZone = ws.config.value?.tenant_timezone;
  // A reload of a list already on screen (every call event triggers one) shows only the masthead's
  // refresh bar; nothing is inserted into the pane, so the list never moves.
  useBackgroundRefresh(history.loaded && history.loading);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const allCalls = new Map<string, Call>();
      for (const c of rows) allCalls.set(c.id, c);
      let cursor = history.nextCursor;
      let pages = 0;
      while (cursor && pages < 40) {
        pages++;
        const env = await ws.api.request<CollectionEnvelope<Call>>('/calls' + query({ limit: 50, ...history.filter, cursor }));
        for (const c of env.data) {
          ws.upsert(c);
          if (matchesHistory(c, filter)) {
            allCalls.set(c.id, c);
          }
        }
        cursor = env.page.next_cursor;
      }
      const list = [...allCalls.values()].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
      const headers = [
        'Date & Time',
        'UTC Time',
        'Rep',
        'Direction',
        'Contact Name',
        'Phone Number',
        'Line Label',
        'Line Number',
        'Outcome',
        'State',
        'Duration (s)',
        'Duration',
        'Hold (s)',
        'Queue (s)',
        'Recording',
        'Call ID',
      ];
      const csvLines = [headers.join(',')];
      for (const c of list) {
        const who = callWho(c, ws.userId, ws.userName);
        const line = resolveCallLine(c, ws.numbers.value);
        const block = callBlock(c);
        const drop = effectiveDrop(c, drops.get(c.id));
        const num = callNumber(c) ?? (c.direction === 'inbound' ? c.from.e164 : c.destination.kind === 'pstn' ? c.destination.e164 : '');
        const talkSec = Math.round((c.talk_ms ?? 0) / 1000);
        const holdSec = Math.round((c.hold_ms ?? 0) / 1000);
        const queueSec = Math.round((c.queue_ms ?? 0) / 1000);
        const rowFields = [
          csvEscape(formatCsvLocalTime(c.created_at, timeZone)),
          csvEscape(c.created_at),
          csvEscape(callRepName(c, ws)),
          csvEscape(c.direction),
          csvEscape(who.name),
          csvEscape(num),
          csvEscape(line?.label ?? ''),
          csvEscape(line?.e164 ?? ''),
          csvEscape(outcomeText(c, block, drop)),
          csvEscape(c.state),
          csvEscape(talkSec),
          csvEscape(formatDuration(c.talk_ms ?? 0)),
          csvEscape(holdSec),
          csvEscape(queueSec),
          csvEscape(c.recording_state),
          csvEscape(c.id),
        ];
        csvLines.push(rowFields.join(','));
      }
      const blob = new Blob(['\ufeff' + csvLines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dayStr = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `sidetone-call-log-${dayStr}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } finally {
      setExporting(false);
    }
  };
  return (
    <>
      <BlocklistInlineStyles />
      <VoicemailDropStyles />
      <div className="pane-head">
        <h1 id="calls-title" className="page-title">Calls</h1>
        <div className="call-log-header-actions">
        <button type="button" className="btn btn-quiet btn-small call-log-export-btn" onClick={() => void exportCsv()} disabled={exporting || rows.length === 0}>
          <Icon icon={Download} /> {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
          {filtered ? <button type="button" className="btn btn-quiet" onClick={() => navigate(historyHref(emptyHistoryFilter(),parsed.view))}>Clear filters</button> : null}
        </div>
      </div>
      <div role="tablist" aria-label="Call view" className="call-tabs">
        <button type="button" role="tab" aria-selected={isAllTab} className="segment" onClick={() => category('')}>
          All
        </button>
        <button type="button" role="tab" aria-selected={isMissedTab} className="segment" onClick={() => category('inbound','missed')}>
          Missed
        </button>
        <button type="button" role="tab" aria-selected={isNotConnectedTab} className="segment" onClick={()=>navigate(historyHref({...filter,direction:'outbound',state:'',outcome:'',outcome_group:'not_connected',final_only:undefined,duration:undefined},'list'))}>
          Not connected
        </button>
        <button type="button" role="tab" aria-selected={isInboundTab} className="segment" onClick={() => category('inbound')}>
          Inbound
        </button>
        <button type="button" role="tab" aria-selected={isOutboundTab} className="segment" onClick={() => category('outbound')}>
          Outbound
        </button>
        <button type="button" role="tab" aria-selected={logView} className="segment" onClick={() => navigate(historyHref(filter,'log'))}>
          Call Log
        </button>
      </div>
      <details className="call-log-filters">
        <summary>Filters {filtered?<span className="chip">Active</span>:null}</summary>
        <div className="filters" role="group" aria-label="Filter calls">
          <label className="filter">
            <span>Direction</span>
            <select value={filter.direction} onChange={e => setFilter({ direction: e.target.value as HistoryFilter['direction'] })}>
              <option value="">All</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
              <option value="internal">Internal</option>
            </select>
          </label>
          <label className="filter">
            <span>State</span>
            <select value={filter.state} onChange={e => setFilter({ state: e.target.value as HistoryFilter['state'] })}>
              <option value="">All</option>
              <option value="ended">Ended</option>
              <option value="failed">Failed</option>
              <option value="dropping">Voicemail drop in progress</option>
              <option value="active">Active</option>
              <option value="held">On hold</option>
              <option value="ringing">Ringing</option>
              <option value="created">Starting</option>
            </select>
          </label>
          <label className="filter">
            <span>Outcome</span>
            <select value={filter.outcome ?? ''} onChange={e => setFilter({ outcome: e.target.value as NonNullable<HistoryFilter['outcome']> })}>
              <option value="">All</option>
              <option value="answered">Answered</option>
              <option value="missed">Unanswered</option>
              <option value="busy">Busy</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
              <option value="blocked">Blocked</option>
              <option value="voicemail_dropped">Voicemail dropped</option>
              <option value="voicemail_drop_interrupted">Voicemail drop interrupted</option>
            </select>
          </label>
        </div>
      <div className="call-log-toolbar">
        <label className="call-log-rep-filter"><span>From (local time)</span><input type="datetime-local" value={localDateInput(filter.from)} onChange={e=>setFilter({from:dateInputIso(e.target.value)})} /></label>
        <label className="call-log-rep-filter"><span>Before (local time)</span><input type="datetime-local" value={localDateInput(filter.to)} onChange={e=>setFilter({to:dateInputIso(e.target.value)})} /></label>
        {(ws.numbers.value?.length??0)>0?<label className="call-log-rep-filter"><span>Line</span><select value={filter.number_id??''} onChange={e=>setFilter({number_id:e.target.value||undefined})}><option value="">All lines</option>{ws.numbers.value?.map(n=><option key={n.id} value={n.id}>{n.label} {formatPhone(n.e164)}</option>)}</select></label>:null}
        {reps.length > 1 || !!repFilter ? (
          <label className="call-log-rep-filter">
            <span>Rep</span>
            <select value={repFilter} onChange={e => setFilter({user_id:e.target.value||undefined,person_scope:e.target.value?'activity':undefined})} aria-label="Filter by rep">
              <option value="">All reps</option>
              {repFilter&&!reps.some(r=>r.id===repFilter)?<option value={repFilter}>Selected person</option>:null}
              {reps.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
        ) : null}
      </div>
      </details>
      <form className="call-log-search" onSubmit={e=>{e.preventDefault();const number=parsePhoneInput(phoneSearch);if(phoneSearch.trim()&&!number.ok){setPhoneError(phoneParseMessage(number));return;}setPhoneError('');setFilter({phone_number:phoneSearch.trim()&&number.ok?number.e164:undefined});}}>
        <label className="field"><span>Find calls with a number</span><input type="search" inputMode="tel" value={phoneSearch} onChange={e=>setPhoneSearch(e.target.value)} placeholder="Phone number" /></label><button type="submit" className="btn">Search calls</button>
        {phoneError?<p className="field-error" role="alert">{phoneError}</p>:null}
      </form>
      {parsed.error?<p className="notice notice-end" role="alert">{parsed.error}<button className="btn" type="button" onClick={()=>navigate(historyHref({},parsed.view))}>Clear filters</button></p>:null}
      {filtered?<p className="call-log-scope">
        {filter.outcome==='missed'||filter.outcome_group==='unanswered'||filter.outcome_group==='not_connected'?unansweredScope(filter.direction):<>{filter.direction?`${filter.direction[0]!.toUpperCase()+filter.direction.slice(1)} calls. `:null}{filter.outcome?`${filter.outcome.replaceAll('_',' ')} outcome. `:null}</>}
        {repFilter?`Person: ${reps.find(r=>r.id===repFilter)?.name??'Selected person'}. `:null}{filter.number_id?`Line: ${ws.numbers.value?.find(n=>n.id===filter.number_id)?.label??'Selected line'}. `:null}
        {filter.from||filter.to?<>Calls created {filter.from?`from ${formatAbsolute(filter.from,timeZone)}`:''} {filter.to?`before ${formatAbsolute(filter.to,timeZone)}`:''}. </>:null}
        {filter.outcome_group==='other'?'Other outcomes and calls still in progress. ':filter.outcome_group==='eligible'?'Finished calls with known outcomes, excluding cancellations. ':filter.outcome_group==='unanswered'||filter.outcome_group==='not_connected'?'Finished calls only. ':null}
        {filter.final_only?'Finished calls only. ':null}{filter.duration?`Calls with ${filter.duration} time. `:null}{filter.person_scope==='handled'?'Handled by the selected person. ':null}
        Only calls your role can access are shown.
      </p>:null}
      {history.error ? <ErrorPanel error={history.error} onRetry={() => ws.loadHistory()} compact /> : null}
      {!history.loaded && history.loading ? <SkeletonRows label="Loading calls" /> : null}
      {history.loaded && rows.length === 0 && !history.error ? (
        filtered || repFilter ? (
          <StatePanel variant="filtered" title="Nothing matches these filters" compact />
        ) : (
          <StatePanel variant="empty" title="No calls yet" body="Calls you make or receive appear here." compact />
        )
      ) : null}
      {rows.length > 0 && logView ? (
        <CallLogTable rows={rows} selectedId={selectedId} timeZone={timeZone} />
      ) : rows.length > 0 ? (
        <ul className="rows" aria-label="Call history">
          {rows.map(call => <CallRow key={call.id} call={call} selected={call.id === selectedId} />)}
        </ul>
      ) : null}
      {history.nextCursor ? (
        <div className="list-foot">
          <button type="button" className="btn" onClick={ws.loadMoreHistory} disabled={history.loading} aria-busy={history.loading || undefined}>{history.loading ? 'Loading…' : 'Load more calls'}</button>
        </div>
      ) : null}
      <FreshnessLine />
    </>
  );
}

/** Live updates are down: said at the foot of the pane, in a slot that is always there (ui/StaleNote.tsx). */
function FreshnessLine() {
  const { stream } = useWorkspace();
  if (!streamIsDown(stream)) return <StaleNote text={null} />;
  const at = stream.lastEventAt ? new Date(stream.lastEventAt).toLocaleTimeString() : null;
  return <StaleNote text={at ? `As of ${at}. Live updates are reconnecting.` : 'Live updates are reconnecting. The list may be out of date.'} />;
}

function LineHistoryLink({numberId,className,children}:{numberId:string|null;className?:string;children:ReactNode}){
 return numberId?<Link to={historyHref({number_id:numberId})} className={className} title="View all calls using this workspace line">{children}</Link>:<span className={className}>{children}</span>;
}

function CallLogTable({ rows, selectedId, timeZone }: { rows: Call[]; selectedId: string | null; timeZone?: string | undefined }) {
  const ws = useWorkspace();
  const {search,navigate}=useRouter();
  const drops = useLocalDrops();
  return (
    <div className="call-log-wrap">
      <table className="call-log-table" aria-label="Call log">
        <thead>
          <tr>
            <th scope="col">Date &amp; Time</th>
            <th scope="col">Rep</th>
            <th scope="col">Direction</th>
            <th scope="col">Contact / Number</th>
            <th scope="col">Line</th>
            <th scope="col">Outcome</th>
            <th scope="col">Duration</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(call => {
            const who = callWho(call, ws.userId, ws.userName);
            const line = resolveCallLine(call, ws.numbers.value);
            const block = callBlock(call);
            const drop = effectiveDrop(call, drops.get(call.id));
            const missed = call.outcome === 'missed' || call.state === 'failed';
            const num = callNumber(call) ?? (call.direction === 'inbound' ? call.from.e164 : call.destination.kind === 'pstn' ? call.destination.e164 : null);
            const selected = call.id === selectedId;
            return (
              <tr key={call.id} className={selected ? 'is-selected' : undefined} onClick={event=>{if((event.target as HTMLElement).closest('a,button,input,select,textarea')||window.getSelection()?.toString())return;navigate(historyDetailHref(call.id,search));}}>
                <td data-label="Date and time" className="mono call-log-time">
                  <Link to={historyDetailHref(call.id,search)}><time dateTime={call.created_at}>{formatLogTimestamp(call.created_at, timeZone)}</time></Link>
                </td>
                <td data-label="Rep" className="call-log-rep">{callRepName(call, ws)}</td>
                <td data-label="Direction" className={missed ? 'tone-end' : undefined}>{directionLabel(call, ws.userId)}</td>
                <td data-label="Contact / number">
                  <div className="call-log-party">
                    {num&&formatPhone(num)===who.name?<PhoneNumberActions number={num} call={call} noteSeed={blockNote(call,timeZone)}/>:<>
                      <Link to={historyDetailHref(call.id,search)} className="call-log-party-name">{who.name}</Link>
                      {num?<PhoneNumberActions number={num} call={call} noteSeed={blockNote(call,timeZone)}/>:null}
                    </>}
                  </div>
                </td>
                <td data-label="Workspace line">
                  {line ? (
                    <LineHistoryLink numberId={line.numberId} className="call-log-line">
                      <span>{line.label}</span>
                      <span className="mono call-log-party-num">{formatPhone(line.e164)}</span>
                    </LineHistoryLink>
                  ) : '-'}
                </td>
                <td data-label="Outcome" className={missed ? 'tone-end' : undefined}>{outcomeText(call, block, drop)}</td>
                <td data-label="Duration" className="mono num">{call.talk_ms > 0 ? formatDuration(call.talk_ms) : '-'}</td>
                <td data-label="Actions">
                  {!num?<CallbackButton call={call} />:null}
                  <Link to={historyDetailHref(call.id,search)} className="btn btn-quiet btn-small">
                    View
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CallRow({ call, selected }: { call: Call; selected: boolean }) {
  const ws = useWorkspace();
  const {search}=useRouter();
  const operator = isOperatorMode(ws.session);
  const who = callWho(call, ws.userId, ws.userName);
  const DirIcon = call.direction === 'inbound' ? ArrowDownLeft : call.direction === 'outbound' ? ArrowUpRight : ArrowLeftRight;
  const missed = call.outcome === 'missed' || call.state === 'failed';
  const block = callBlock(call);
  const drop = effectiveDrop(call, useLocalDrops().get(call.id));
  const number = callNumber(call);
  // Call line identity (UX spec 3.3): which of our numbers the call used, as swatch, label, and actual call-bound number.
  const resolvedLine = resolveCallLine(call, ws.numbers.value);
  return (
    <li className="call-ledger-item" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0 8px', borderBottom: '1px solid var(--rule)' }}>
      <Link to={historyDetailHref(call.id,search)} className={`row${selected ? ' is-selected' : ''}`} style={{ flex: '1 1 260px', minWidth: 0, borderBottom: 0 }} aria-current={selected ? 'page' : undefined}>
        <span className="row-line1">
          {call.state === 'ringing' || call.state === 'active' || call.state === 'held' ? <span className={`dot dot-${call.state === 'held' ? 'hold' : call.state === 'ringing' ? 'ring' : 'live'}`} aria-hidden="true" /> : null}
          <span className="row-name">{who.name}</span>
          {who.company ? <span className="row-company">{who.company}</span> : null}
          {who.number ? <span className="row-number mono">{who.number}</span> : null}
          <time className="row-time" dateTime={call.created_at} title={formatAbsolute(call.created_at)} aria-label={formatAbsolute(call.created_at)}>{formatRelative(call.created_at)}</time>
        </span>
        <span className="row-line2">
          <span className={`row-dir${missed ? ' tone-end' : ''}`}><Icon icon={DirIcon} /> {directionLabel(call, ws.userId)}</span>
          <span className="sep" aria-hidden="true" />
          <span>{outcomeText(call, block, drop)}</span>
          {call.talk_ms > 0 ? <><span className="sep" aria-hidden="true" /><span className="num">{formatDuration(call.talk_ms)}</span></> : null}
          {resolvedLine ? <><span className="sep" aria-hidden="true" /><CallLineIdentity call={call} numbers={ws.numbers.value} /></> : null}
          {block ? (
            <span className="row-badge bl-reason-badge" title={block.reason ?? undefined}>
              <Icon icon={Ban} /> <span>{block.reason ? clipReason(block.reason, 90) : block.list ? LIST_LABEL[block.list] : 'Blocked list'}</span>
            </span>
          ) : drop ? (
            // The drop says how the call ended (the system hangs up after it), so it replaces the end label.
            <span className="row-badge vmd-badge" title={dropBadge(drop)}>
              <Icon icon={Voicemail} /> <span>{dropBadge(drop)}</span>
            </span>
          ) : operator ? <TerminationLabel call={call} /> : null}
          {call.recording_state === 'ready' || call.recording_state === 'recording' ? <span className="row-badge"><Icon icon={Radio} /> Recording</span> : null}
          {call.transcription_state === 'complete' || call.transcription_state === 'live' ? <span className="row-badge"><Icon icon={Captions} /> Transcript</span> : null}
        </span>
      </Link>
      <div className="call-ledger-actions" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', marginLeft: 'auto' }}>
      {number?<PhoneNumberActions number={number} call={call} showNumber={false} noteSeed={blockNote(call,ws.config.value?.tenant_timezone)}/>:<CallbackButton call={call} />}
      </div>
    </li>
  );
}

function useResource<T>(path: string | null, deps: unknown[]): { value: T | null; error: ErrorView | null; loading: boolean; reload: () => void } {
  const { api } = useWorkspace();
  const [state, setState] = useState<{ value: T | null; error: ErrorView | null; loading: boolean }>({ value: null, error: null, loading: !!path });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) { setState({ value: null, error: null, loading: false }); return; }
    const controller = new AbortController();
    setState(s => ({ ...s, loading: true, error: null }));
    api.request<T>(path, { signal: controller.signal }).then(
      value => setState({ value, error: null, loading: false }),
      error => { if (!isAbortError(error)) setState({ value: null, error: describeError(error, "Couldn't load this"), loading: false }); },
    );
    return () => controller.abort();
  }, [api, path, tick, ...deps]);
  return { ...state, reload: () => setTick(t => t + 1) };
}

export function CallDetail({ callId }: { callId: string }) {
  const ws = useWorkspace();
  const { navigate,search } = useRouter();
  const cached = ws.calls.get(callId) ?? null;
  const fetched = useResource<DataEnvelope<Call>>(`/calls/${encodeURIComponent(callId)}`, [callId]);
  useEffect(() => { if (fetched.value) ws.upsert(fetched.value.data); }, [fetched.value]);
  const call = ws.calls.get(callId) ?? cached;
  const access = blocklistAccess(ws.permissions);
  const blocklist = useMemo(() => new BlocklistClient(ws.api), [ws.api]);
  const [notes, setNotes] = useState<{ callId: string; list: CallNote[]; rev: number }>({ callId, list: [], rev: 0 });
  const localDrop = useLocalDrops().get(callId);
  if (!call) {
    if (fetched.error) return <ErrorPanel error={fetched.error} onRetry={fetched.reload} />;
    return <SkeletonRows rows={3} label="Loading call" />;
  }
  const operator = isOperatorMode(ws.session);
  const who = callWho(call, ws.userId, ws.userName);
  const crmLink = callCrmLink(call);
  const endedBy = endedByLine(call);
  const block = callBlock(call);
  const number = callNumber(call);
  const tz = ws.config.value?.tenant_timezone;
  const resolvedLine = resolveCallLine(call, ws.numbers.value);
  const callNotes = notes.callId === call.id ? notes : { callId: call.id, list: [], rev: 0 };
  const drop = effectiveDrop(call, localDrop);
  const played = drop ? playedText(drop) : null;
  const hasRecording = call.recording_state !== 'off' && call.recording_state !== 'failed';
  const hasTranscript = call.transcription_state !== 'off';
  // Transcripts only: a transcribed call with no recording shows the plain "Not recorded (transcripts only)" row.
  const showRecording = operator || transcriptsOnly(call) || (hasRecording && (ws.mediaAccess.state !== 'ready' || hasPermission(ws.permissions, 'recordings:read')));
  const showTranscript = operator || (hasTranscript && (ws.mediaAccess.state !== 'ready' || hasPermission(ws.permissions, 'transcripts:read')));
  return (
    <article className="detail" aria-labelledby="call-detail-title">
      <button type="button" className="btn btn-quiet" onClick={() => navigate('/inbox/calls'+(search.toString()?'?'+search.toString():''))}><Icon icon={ChevronLeft} /> Back to calls</button>
      <CallContactNameEditor call={call} displayedName={who.name} />
      {who.company || who.number ? (
        <p className="detail-party-sub">
          {who.company ? <span>{who.company}</span> : null}
          {who.company && who.number ? <span className="sep" aria-hidden="true" /> : null}
          {who.number ? <span className="mono">{who.number}</span> : null}
        </p>
      ) : null}
      <dl className="facts">
        <div><dt>Direction</dt><dd>{directionLabel(call, ws.userId)}</dd></div>
        <div><dt>Outcome</dt><dd>{outcomeText(call, block, drop)}</dd></div>
        {drop ? (
          <>
            <div><dt>Voicemail drop</dt><dd>{drop.name ?? 'Name not recorded'}</dd></div>
            <div><dt>Drop result</dt><dd>{dropResultText(drop)}</dd></div>
            {played ? <div><dt>Played</dt><dd className="num">{played}</dd></div> : null}
            {drop.startedAt ? <div><dt>Dropped at</dt><dd>{formatAbsolute(drop.startedAt, tz)}</dd></div> : null}
          </>
        ) : null}
        <div><dt>Started</dt><dd>{formatAbsolute(call.created_at, ws.config.value?.tenant_timezone)}</dd></div>
        <div><dt>Talk time</dt><dd className="num">{formatDuration(call.talk_ms)}</dd></div>
        {resolvedLine ? <div><dt>Number</dt><dd><CallLineIdentity call={call} numbers={ws.numbers.value} /></dd></div> : null}
      </dl>
      {endedBy ? <p className="detail-ended-by">{endedBy}</p> : null}
      <p className="detail-crm-link">{number?<PhoneNumberActions number={number} call={call} noteSeed={blockNote(call,tz)}/>:<CallbackButton call={call} detail />}</p>
      {crmLink ? (
        <p className="detail-crm-link">
          <a href={crmLink.href} target="_blank" rel="noopener noreferrer" className="btn btn-quiet">
            <Icon icon={ExternalLink} /> <span>{crmLink.label}</span> <span className="meta">({crmLink.host})</span>
          </a>
        </p>
      ) : null}
      <CallSummarySection call={call} block={block} drop={drop} notes={callNotes.list} operator={operator} selfUserId={ws.userId} timeZone={tz} />
      {block ? <BlockedNotice call={call} block={block} canOpenList={access.read} /> : null}
      {number && access.read ? <NumberStatus showControl={false} key={number} e164={number} client={blocklist} access={access} noteSeed={blockNote(call, tz)} label={`Blocked list status for ${formatPhone(number)}`} /> : null}
      {call.state === 'active' || call.state === 'held' ? <CaptureBand call={call} headingLevel={3} /> : null}
      {/* Where no call window can open (the desktop app, iPhone), the live coach is here; the dock links to this page.
          Never in a browser that has the call window: one coach poller per rep (GET /coach is 30 a minute). */}
      {(call.state === 'active' || call.state === 'held') && !callWindowSupported() ? <CoachPanel call={call} /> : null}
      <PostCallFollowupSection key={`followup-${call.id}`} call={call} />
      <CallNotesSection key={call.id} call={call} onChanged={(list, mutated) => setNotes(n => ({ callId: call.id, list, rev: mutated && n.callId === call.id ? n.rev + 1 : n.rev }))} />
      {operator ? (
        <details className="diagnostics operator-tech-details">
          <summary>Show technical details</summary>
          <dl className="facts">
            <div><dt>Hold time</dt><dd className="num">{formatDuration(call.hold_ms)}</dd></div>
            <div><dt>From</dt><dd>{call.from.e164 ? call.direction==='inbound'?<PhoneNumberActions number={call.from.e164} call={call}/>:<LineHistoryLink numberId={call.from.number_id}>{formatPhone(call.from.e164)}</LineHistoryLink> : call.direction === 'internal' ? 'Internal, no number' : 'Not recorded'}</dd></div>
          </dl>
          {call.state !== 'active' && call.state !== 'held' && call.direction !== 'internal' ? <CaptureBand call={call} headingLevel={3} /> : null}
          <h3 className="section-title">Participants</h3>
          {call.participants.length === 0 ? <p className="field-hint">No participants recorded.</p> : (
            <ul className="plain-list">
              {call.participants.map(p => (
                <li key={p.id}>
                  <strong>{p.kind === 'external' ? 'External party' : ws.userName(p.user_id) ?? 'Teammate'}</strong>
                  <span className="meta"> {p.joined_at ? `joined ${formatAbsolute(p.joined_at)}` : 'did not join'}{p.left_at ? `, left ${formatAbsolute(p.left_at)}` : ''}</span>
                </li>
              ))}
            </ul>
          )}
          <WhatHappenedSection call={call} refreshToken={callNotes.rev} notes={callNotes.list} />
        </details>
      ) : null}
      {showRecording ? <RecordingSection call={call} /> : null}
      {showTranscript ? <TranscriptSection call={call} /> : null}
    </article>
  );
}

/** A refused call: which list refused it, the reason, and the way to the entry. */
function BlockedNotice({ call, block, canOpenList }: { call: Call; block: CallBlock; canOpenList: boolean }) {
  // The list has no single-entry route: the page searches for the number and opens the entry it finds.
  const number = callNumber(call);
  const href = block.entryId ? `/blocklist?${new URLSearchParams(number ? { q: number, entry: block.entryId } : { entry: block.entryId }).toString()}` : null;
  return (
    <div className="notice bl-blocked" role="note" aria-label="Blocked call">
      <BlocklistInlineStyles />
      <Icon icon={Ban} />
      <div className="bl-blocked-body">
        <p><strong>Blocked call.</strong> {blockSentence(call, block)}</p>
        {block.reason ? <p>Reason: {block.reason}</p> : null}
        {canOpenList && href ? <p><Link to={href}>Open the list entry</Link></p> : null}
      </div>
    </div>
  );
}

function RecordingSection({ call }: { call: Call }) {
  const ws = useWorkspace();
  const operator = isOperatorMode(ws.session);
  const allowed = hasPermission(ws.permissions, 'recordings:read');
  const cap = capability(ws.config.value, 'recording.capture');
  const playback = capability(ws.config.value, 'recording.playback');
  const node = recorderNode(call, cap);
  // Transcripts only (2026-10-29): recorder 'off' with a transcript. 'off' alone does not prove there is no audio (an older
  // call recorded until consent was withdrawn also ends 'off'), so recordings are still fetched and any found are played;
  // the plain "Not recorded (transcripts only)" state replaces only the empty list and the no-access panel.
  const transcribedOnly = transcriptsOnly(call);
  const recordings = useSensitiveResource<CollectionEnvelope<RecordingWithProvenance>>(allowed ? `/calls/${encodeURIComponent(call.id)}/recordings` : null, call.recording_state);
  const mergedRecordings = coalesceCallRecordings(recordings.value?.data ?? []);
  const transcriptsOnlyPanel = <StatePanel variant="empty" compact headingLevel={3} title={TRANSCRIPTS_ONLY_TEXT} />;
  return (
    <section className="detail-section" aria-labelledby="rec-title">
      <h3 id="rec-title" className="section-title">Recording</h3>
      {!transcribedOnly && (operator || (node.text !== 'Not set up' && node.text !== 'Off')) ? <p className="meta">Recorder: {node.text}</p> : null}
      {ws.mediaAccess.state !== 'ready' ? (
        <AccessPending state={ws.mediaAccess.state} />
      ) : !allowed && transcribedOnly ? (
        transcriptsOnlyPanel
      ) : !allowed ? (
        <StatePanel variant="denied" compact headingLevel={3} title="You don't have playback access" body="Your role doesn't include listening to recordings." details={{ permission: 'recordings:read' }} />
      ) : recordings.status === 'loading' ? <SkeletonRows rows={1} label="Loading recordings" /> : recordings.error ? <ErrorPanel error={recordings.error} onRetry={recordings.reload} compact /> : mergedRecordings.length === 0 && transcribedOnly ? (
        transcriptsOnlyPanel
      ) : mergedRecordings.length === 0 ? (
        <>
          <StatePanel variant={gate(cap).render === 'explain' ? 'unconfigured' : 'empty'} compact headingLevel={3} title={gate(cap).render === 'explain' ? "Recording isn't set up for this workspace" : 'No recording for this call'} body={gate(cap).render === 'explain' ? 'Calls still work. Recordings appear here once recording is set up.' : undefined} />
          <Diagnostics diagnostic={gateDiagnostic(gate(cap), cap?.reason_code ?? null)} />
        </>
      ) : (
        <ul className="plain-list" aria-label="Recordings for this call">
          {mergedRecordings.map(r => (
            <li key={r.id} className="recording-item">
              <strong>{recordingStatus(r)}</strong>
              <span className="meta"> {formatDuration(r.duration_ms)}, {r.channels} channel{r.channels === 1 ? '' : 's'}</span>
              <p className="meta">{retentionLine(r, ws.config.value?.tenant_timezone)}</p>
              <RecordingMedia recording={r} playback={playback} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Shown instead of any recording or transcript content while access is being re-checked. */
function AccessPending({ state }: { state: 'refreshing' | 'blocked' }) {
  return state === 'refreshing' ? (
    <p className="field-hint" role="status">Your access changed. Checking what you can see…</p>
  ) : (
    <p className="inline-reason" role="status">Sign in again to see recordings and transcripts.</p>
  );
}

function recordingStatus(r: Recording): string {
  if (r.status === 'processing' || r.status === 'starting' || r.status === 'capturing' || r.status === 'finalized') return 'Finishing recording…';
  if (r.status === 'failed') return 'Recording failed';
  if (r.status === 'partial') return 'Incomplete recording';
  if ((r.status === 'ready' || r.status === 'verified') && r.available) return 'Ready';
  if (r.status === 'uploading' || r.status === 'replicating' || r.status === 'encrypted' || r.status === 'replicated') return 'Saving to archive…';
  return r.status === 'ready' || r.status === 'verified' ? 'Saved, not yet playable here' : `Status: ${r.status}`;
}

function TranscriptSection({ call }: { call: Call }) {
  const ws = useWorkspace();
  const operator = isOperatorMode(ws.session);
  const allowed = hasPermission(ws.permissions, 'transcripts:read');
  const transcript = useCallTranscript(call);
  // The segment count is known once the transcript loads; until then a 'delayed' call reads "automatic, may have gaps".
  const loaded = transcript.state.transcript;
  const status = transcriptStatusLine(call, capability(ws.config.value, 'transcription.live'), loaded ? loaded.segments.length : undefined);
  return (
    <section className="detail-section" aria-labelledby="tr-title">
      <h3 id="tr-title" className="section-title">Transcript</h3>
      {operator || (status.text !== 'Not set up' && !status.text.startsWith('Off')) ? (
        <p className={`meta${status.tone === 'warn' ? ' tone-warn' : ''}`}>Transcription: {status.text}</p>
      ) : null}
      <AskPanel key={call.id} call={call} />
      {ws.mediaAccess.state !== 'ready' ? (
        <AccessPending state={ws.mediaAccess.state} />
      ) : !allowed ? (
        <StatePanel variant="denied" compact headingLevel={3} title="You don't have transcript access" body="Your role doesn't include reading transcripts." details={{ permission: 'transcripts:read' }} />
      ) : call.transcription_state === 'off' ? null : (
        <CallTranscriptBody call={call} status={status} transcript={transcript} />
      )}
    </section>
  );
}
